"use client";

import { motion } from "framer-motion";
import { MapPin } from "lucide-react";
import Link from "next/link";
import { cities } from "@/lib/config/cities";
import type { AreaEntry } from "@/lib/config/cities";

import type { Locale } from "./templates/ServicePageTemplate";

function AreaList({
  areas,
  delay = 0,
  keywordSlug,
  highlightMunicipality,
  locale = "sv",
  linkedAreaSlugs,
}: {
  areas: AreaEntry[];
  delay?: number;
  keywordSlug?: string;
  highlightMunicipality?: string | null;
  locale?: Locale;
  linkedAreaSlugs?: Record<string, string>;
}) {
  const prefix = locale === "en" ? "/en" : "";
  return (
    <div className="columns-2 sm:columns-3 lg:columns-4 gap-x-6">
      {areas.map((area, i) => {
        const isHighlighted = highlightMunicipality === area.name;
        const href = keywordSlug
          ? `${prefix}/${keywordSlug}-${area.slug}`
          : linkedAreaSlugs?.[area.slug];

        const content = (
          <>
            <MapPin className="h-4 w-4 text-brand-secondary shrink-0" />
            <span
              className={`text-sm transition-colors ${
                isHighlighted
                  ? "text-brand-secondary font-semibold"
                  : "text-brand-dark group-hover:text-brand-secondary"
              }`}
            >
              {area.name}
            </span>
          </>
        );

        return (
          <motion.div
            key={area.slug}
            initial={{ opacity: 0, y: 8 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.25, delay: delay + i * 0.02 }}
            className="flex items-center gap-2.5 mb-3 break-inside-avoid group"
          >
            {href ? (
              <Link
                href={href}
                className="flex items-center gap-2.5 hover:text-brand-secondary transition-colors"
              >
                {content}
              </Link>
            ) : (
              content
            )}
          </motion.div>
        );
      })}
    </div>
  );
}

interface AvailableAreasProps {
  heading?: string;
  highlightMunicipality?: string | null;
  keywordSlug?: string;
  locale?: Locale;
  linkedAreaSlugs?: Record<string, string>;
}

export default function AvailableAreas({
  heading = "Få hjälp med bortforsling i dessa kommuner",
  highlightMunicipality = null,
  keywordSlug,
  locale = "sv",
  linkedAreaSlugs,
}: AvailableAreasProps = {}) {
  const cityKeys = ["stockholm", "göteborg"] as const;

  return (
    <section className="py-8 sm:pt-10 sm:pb-24 bg-[#F5F5F5]">
      <div className="mx-auto max-w-screen-2xl px-6 xl:px-14">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5 }}
          className="text-xl sm:text-2xl font-bold text-brand-dark mb-12 sm:mb-16"
        >
          {heading}
        </motion.h2>

        <div className="space-y-12 sm:space-y-16">
          {cityKeys.map((key, cityIdx) => {
            const data = cities[key];
            return (
              <div key={key}>
                <motion.h3
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4 }}
                  className="text-base sm:text-lg font-semibold text-brand-dark mb-6"
                >
                  {locale === "en" ? data.nameEn : data.name}
                </motion.h3>
                <motion.h4
                  initial={{ opacity: 0, y: 8 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3 }}
                  className="text-sm font-medium text-brand-dark/60 uppercase tracking-wide mb-4"
                >
                  {locale === "en" ? "Municipalities" : "Kommuner"}
                </motion.h4>
                <AreaList
                  areas={data.kommuner}
                  delay={cityIdx * 0.1}
                  keywordSlug={keywordSlug}
                  highlightMunicipality={highlightMunicipality}
                  locale={locale}
                  linkedAreaSlugs={linkedAreaSlugs}
                />
                <motion.h4
                  initial={{ opacity: 0, y: 8 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3 }}
                  className="text-sm font-medium text-brand-dark/60 uppercase tracking-wide mt-8 mb-4"
                >
                  {locale === "en" ? "Neighborhoods" : "Stadsdelar"}
                </motion.h4>
                <AreaList
                  areas={data.stadsdelar}
                  delay={cityIdx * 0.1 + 0.05}
                  keywordSlug={keywordSlug}
                  highlightMunicipality={highlightMunicipality}
                  locale={locale}
                  linkedAreaSlugs={linkedAreaSlugs}
                />
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
